/**
 * Canonical route map for the plcy.io marketing site.
 *
 * @remarks
 * The markdown copy decks in docs/marketing-pages/ link pages by bare slug
 * (e.g. `/ai`, `/coverage-tower`). Builders MUST translate those slugs to
 * real routes through {@link ROUTES} — never hardcode a path in a page.
 *
 * Soft launch: every marketing page lives under the unlisted `/preview`
 * prefix. The public landing at `/` is the coming-soon page and never
 * links here — `/preview` is reachable only by direct deep link.
 */
export const ROUTES = {
  home: '/preview',
  ai: '/preview/product/ai',
  'ai-governance': '/preview/platform/ai-governance',
  'workflow-automation': '/preview/product/workflow-automation',
  'submission-to-bind': '/preview/product/submission-to-bind',
  'coverage-tower': '/preview/product/coverage-tower',
  'premium-accounting': '/preview/product/premium-accounting',
  billing: '/preview/product/billing',
  commissions: '/preview/product/commissions',
  'compliance-back-office': '/preview/product/compliance-back-office',
  portals: '/preview/product/portals',
  platform: '/preview/platform',
  integrations: '/preview/platform/integrations',
  observability: '/preview/platform/observability',
  security: '/preview/platform/security',
  'mga-mgu': '/preview/solutions/mga-mgu',
  'wholesale-brokers': '/preview/solutions/wholesale-brokers',
} as const;

/** A deck slug — the keys of {@link ROUTES}. */
export type RouteSlug = keyof typeof ROUTES;

/** A single navigation entry (used by the header nav and footer). */
export interface NavLink {
  /** Route path from {@link ROUTES}. */
  readonly to: string;
  /** Human-readable label. */
  readonly label: string;
}

/** A grouped set of navigation links (one nav dropdown / footer column). */
export interface NavGroup {
  readonly label: string;
  readonly links: readonly NavLink[];
}

/** Header dropdown + footer column structure for the whole site. */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Product',
    links: [
      { to: ROUTES.ai, label: 'AI Intelligence' },
      { to: ROUTES['workflow-automation'], label: 'Workflow Automation' },
      { to: ROUTES['submission-to-bind'], label: 'Submission to Bind' },
      { to: ROUTES['coverage-tower'], label: 'Coverage Tower & Placement' },
      { to: ROUTES['premium-accounting'], label: 'Premium Accounting' },
      { to: ROUTES.billing, label: 'Billing & Invoicing' },
      { to: ROUTES.commissions, label: 'Commissions & Compensation' },
      {
        to: ROUTES['compliance-back-office'],
        label: 'Compliance & Back Office',
      },
      { to: ROUTES.portals, label: 'Portals & Experience' },
    ],
  },
  {
    label: 'Platform',
    links: [
      { to: ROUTES.platform, label: 'Architecture' },
      { to: ROUTES.integrations, label: 'Integrations & APIs' },
      { to: ROUTES.observability, label: 'Observability' },
      { to: ROUTES['ai-governance'], label: 'Governed AI' },
      { to: ROUTES.security, label: 'Security & Compliance' },
    ],
  },
  {
    label: 'Solutions',
    links: [
      { to: ROUTES['mga-mgu'], label: 'For MGAs & MGUs' },
      { to: ROUTES['wholesale-brokers'], label: 'For Wholesale Brokers' },
    ],
  },
] as const;
